/**
 * R2AS upstairs TV — World Cup 2026 final highlights.
 * Blueprint: docs/R2AS_TV_WORLD_CUP.md
 *
 * SoftPlan screen mesh: floor_0_28 (black panel). Embeds only — no hosted FIFA files.
 */

/** @typedef {{ youtubeId: string, label: string }} TvClip */

/**
 * Argentina vs Spain · WC26 final (2026-07-19) — clips & highlights playlist.
 * Update IDs as better official packages appear on YouTube.
 *
 * @type {TvClip[]}
 */
export const R2AS_TV_PLAYLIST = [
  {
    youtubeId: '1uE2o-WqyTI',
    label: 'Argentina vs Spain · Final highlights',
  },
  {
    youtubeId: '4llJ3HJAwqk',
    label: 'Spain 1–0 Argentina · Torres extra-time winner',
  },
  {
    youtubeId: 'FuG_OjPLd6w',
    label: 'Final highlights short',
  },
  {
    youtubeId: '15uKZ50UE7w',
    label: 'Rodri Golden Ball · FOX',
  },
  {
    youtubeId: 'EcRUQ4nA9u8',
    label: 'Yamal & Messi after the final · FOX',
  },
  {
    youtubeId: 'QMA9pEtkKIM',
    label: 'Cucurella almost-goal moment · FOX',
  },
  {
    youtubeId: 'MpqsnvLiXKI',
    label: 'Medal ceremony after Spain win',
  },
];

export const R2AS_TV_MATCH = {
  date: '2026-07-19',
  home: 'Argentina',
  away: 'Spain',
  score: '0–1 (a.e.t.)',
  note: 'Spain win World Cup · Ferran Torres 106′',
};

/** Screen overlay pose in LabViewer world meters. */
export const R2AS_TV_SCREEN = {
  /** World meters (LabViewer centered space) */
  position: [-2.305, 5.195, -1.501],
  /** Euler radians — PlaneGeometry / Html face +Z by default */
  rotation: [0, 0, 0],
  width: 1.58,
  height: 0.89,
  meshName: 'floor_0_28',
};

/**
 * Proximity + occlusion audio — docs/R2AS_TV_WORLD_CUP.md
 * Distances are camera ↔ TV center (meters). Volumes are YouTube 0–100.
 *
 * Stair bottom (survey) ≈ 4.7 m from TV — keep at faintVol.
 */
export const R2AS_TV_AUDIO = {
  /** Loud only when basically in front of the TV */
  nearM: 1.7,
  /** Bottom of stairs — faintly audible */
  faintM: 4.9,
  /** Mute beyond */
  silentM: 10,
  nearVol: 70,
  faintVol: 5,
  /** Cap when floor/wall blocks line of sight (under-TV, other rooms) */
  occludedVol: 1,
  /**
   * Falloff exponent on smoothstep t (near→faint).
   * Lower = stays quiet longer / only loud very near the TV (~0.4).
   */
  falloffPow: 0.4,
};

/**
 * Map camera–TV distance (m) → YouTube volume 0–100 (clear LOS).
 * @param {number} distanceM
 * @param {typeof R2AS_TV_AUDIO} [cfg]
 */
export function tvVolumeFromDistance(distanceM, cfg = R2AS_TV_AUDIO) {
  const d = Number(distanceM);
  if (!Number.isFinite(d) || d >= cfg.silentM) return 0;
  if (d <= cfg.nearM) return cfg.nearVol;

  if (d <= cfg.faintM) {
    const t = smoothstep(cfg.nearM, cfg.faintM, d);
    const pow = cfg.falloffPow ?? 0.4;
    // Fast drop away from TV so stair bottom (~faintM) stays faint
    const u = Math.pow(t, pow);
    return Math.round(lerp(cfg.nearVol, cfg.faintVol, u));
  }

  const t = smoothstep(cfg.faintM, cfg.silentM, d);
  return Math.round(lerp(cfg.faintVol, 0, t));
}

/**
 * Final hearing volume: distance curve, capped when geometry occludes the TV.
 * @param {number} distanceM
 * @param {boolean} occluded
 * @param {typeof R2AS_TV_AUDIO} [cfg]
 */
export function tvHearingVolume(distanceM, occluded, cfg = R2AS_TV_AUDIO) {
  const base = tvVolumeFromDistance(distanceM, cfg);
  if (!occluded) return base;
  return Math.min(base, cfg.occludedVol ?? 2);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Build youtube-nocookie embed URL for muted looping playlist (+ JS API).
 * @param {TvClip[]} [playlist]
 */
export function buildTvEmbedUrl(playlist = R2AS_TV_PLAYLIST) {
  const ids = playlist.map((c) => c.youtubeId).filter(Boolean);
  if (!ids.length) return '';
  const start = ids[0];
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    loop: '1',
    playlist: ids.join(','),
    enablejsapi: '1',
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube-nocookie.com/embed/${start}?${params.toString()}`;
}
